import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactTimeAgo from 'react-time-ago';
import { Check, ChevronDown, ChevronRight, Clock, Eye } from 'lucide-react';

import SearchField from '@/component-library/widgets/SearchField/SearchField';
import { ActivityLog } from '@/models/ActivityLogsByWorkspace';
import { CustomCard } from '@/modules/Dashboard/DashboardPage/Components/MetricComponents';

// Group activities by date for session-like view
interface ActivityGroup {
  date: string; // Date string for display
  dateTimestamp: number; // Timestamp for the date (start of day)
  activities: ActivityLog[];
  totalActivities: number;
  timeRange: {
    start: number; // Timestamp
    end: number; // Timestamp
  };
}

interface ActivityLogsPageProps {
  data: ActivityLog[] | undefined;
  changeActiveStep: (step: string) => void;
}

/** Prefer numeric timestamp; fall back to createdAt so grouping works if timestamp was not mapped. */
function activityTimeMs(activity: ActivityLog): number {
  const t = activity.timestamp;
  if (typeof t === 'number' && !Number.isNaN(t) && t > 0) return t;
  if (activity.createdAt) {
    const ms = new Date(activity.createdAt).getTime();
    if (!Number.isNaN(ms) && ms > 0) return ms;
  }
  return 0;
}

// Activity name filters based on your data
const activityTypes = ['All Types', 'page_viewed'];

// Page filters based on your data
const pageFilters = ['All Pages', 'audience', 'delivery'];

// Component filters based on your data
const componentFilters = ['All Components', 'risk_distribution_chart'];

const ActivityLogsPage: React.FC<ActivityLogsPageProps> = ({ data }) => {
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState('');
  const [activityTypeFilter, setActivityTypeFilter] = useState('All Types');
  const [pageFilter, setPageFilter] = useState('All Pages');
  const [componentFilter, setComponentFilter] = useState('All Components');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [groupActivities, setGroupActivities] = useState(true);

  // Group activities by date
  const groupActivitiesByDate = (activities: ActivityLog[]): ActivityGroup[] => {
    const groups: { [key: string]: ActivityLog[] } = {};

    activities.forEach((activity) => {
      const timestamp = activityTimeMs(activity);
      if (!timestamp || Number.isNaN(timestamp)) return;

      const date = new Date(timestamp).toDateString();
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(activity);
    });

    return Object.entries(groups)
      .map(([date, dayActivities]) => {
        const sortedActivities = [...dayActivities].sort((a, b) => activityTimeMs(b) - activityTimeMs(a));
        const msList = sortedActivities.map(activityTimeMs).filter((t) => t > 0);
        const rangeStart = msList.length ? Math.min(...msList) : 0;
        const rangeEnd = msList.length ? Math.max(...msList) : 0;

        const anchorMs = sortedActivities[0] ? activityTimeMs(sortedActivities[0]) : 0;
        const dateTimestamp = anchorMs ? new Date(anchorMs).setHours(0, 0, 0, 0) : 0;

        return {
          date,
          dateTimestamp,
          activities: sortedActivities,
          totalActivities: dayActivities.length,
          timeRange: {
            start: rangeStart,
            end: rangeEnd,
          },
        };
      })
      .sort((a, b) => b.dateTimestamp - a.dateTimestamp);
  };

  const filteredActivities = useMemo(() => {
    const filtered =
      data?.filter((activity) => {
        const matchesActivityType =
          activityTypeFilter === 'All Types' || activity.eventType === activityTypeFilter;
        const matchesPage = pageFilter === 'All Pages' || activity.data?.page === pageFilter;
        const matchesComponent =
          componentFilter === 'All Components' || activity.data?.component === componentFilter;
        const matchesSearch =
          searchValue === '' ||
          activity.eventType?.toLowerCase().includes(searchValue.toLowerCase()) ||
          activity.userEmail?.toLowerCase().includes(searchValue.toLowerCase()) ||
          activity.workspaceId?.toLowerCase().includes(searchValue.toLowerCase()) ||
          activity.data?.title?.toLowerCase().includes(searchValue.toLowerCase()) ||
          activity.data?.url?.toLowerCase().includes(searchValue.toLowerCase()) ||
          activity.data?.referrer?.toLowerCase().includes(searchValue.toLowerCase());
        return matchesActivityType && matchesPage && matchesComponent && matchesSearch;
      }) ?? [];
    return [...filtered].sort((a, b) => activityTimeMs(b) - activityTimeMs(a));
  }, [data, activityTypeFilter, pageFilter, componentFilter, searchValue]);

  const groupedActivities = groupActivitiesByDate(filteredActivities);

  // Toggle group expansion
  const toggleGroup = (date: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(date)) {
      newExpanded.delete(date);
    } else {
      newExpanded.add(date);
    }
    setExpandedGroups(newExpanded);
  };

  // Format date from timestamp (supports both number and string for backward compatibility)
  const formatDate = (timestamp: number | string): string => {
    let date: Date;
    if (typeof timestamp === 'number') {
      date = new Date(timestamp);
    } else {
      date = new Date(timestamp);
    }
    return date.toLocaleDateString('en-us', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Format time from timestamp
  const formatTime = (timestamp: number): string => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-us', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Format event type
  const formatEventType = (eventType: string): string => {
    if (!eventType) return 'Activity';
    return eventType.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  };

  // Calculate time span for grouped activities
  const getTimeSpan = (group: ActivityGroup): string => {
    if (group.activities.length === 1) {
      const ms = activityTimeMs(group.activities[0]!);
      return ms > 0 ? formatTime(ms) : '-';
    }
    const start = group.timeRange.start ? formatTime(group.timeRange.start) : '-';
    const end = group.timeRange.end ? formatTime(group.timeRange.end) : '-';
    return `${start} - ${end}`;
  };

  return (
    <CustomCard title="Activity Logs">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <p className="text-custom-darkSilver text-sm">Track user activities and system events</p>

        <div>
          <input
            type="checkbox"
            name="group-activities"
            id="group-activities"
            className="hidden"
            checked={groupActivities}
            onChange={(e) => {
              setGroupActivities(e.target.checked);
            }}
          />
          <label
            htmlFor="group-activities"
            className="flex items-center gap-2 text-custom-darkSilver text-sm font-semibold cursor-pointer"
          >
            <span
              className={`relative inline-block h-4 w-4 rounded-sm border border-custom-blue transition-colors ${
                groupActivities && 'bg-custom-blue'
              }`}
            >
              <Check color="#fff" className="absolute left-0 top-0 w-3.5 h-3.5" />
            </span>
            Group activities `by Date`
          </label>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-8">
        {/* Search */}
        <SearchField initialValue={searchValue} setSearchValue={setSearchValue} withFilter={false} />

        {/* Activity Type Filter */}
        <select
          value={activityTypeFilter}
          onChange={(e) => setActivityTypeFilter(e.target.value)}
          className="max-w-[150px] bg-custom-silver w-fit rounded-lg text-custom-black text-sm px-2"
        >
          {activityTypes.map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </select>

        {/* Page Filter */}
        <select
          value={pageFilter}
          onChange={(e) => setPageFilter(e.target.value)}
          className="w-[150px] bg-custom-silver w-fit rounded-lg text-custom-black text-sm px-2"
        >
          {pageFilters.map((page) => (
            <option key={page} value={page}>
              {page}
            </option>
          ))}
        </select>

        {/* Component Filter */}
        <select
          value={componentFilter}
          onChange={(e) => setComponentFilter(e.target.value)}
          className="max-w-[150px] bg-custom-silver w-fit rounded-lg text-custom-black text-sm px-2"
        >
          {componentFilters.map((component) => (
            <option key={component} value={component}>
              {component}
            </option>
          ))}
        </select>
      </div>

      {/* Content */}
      <div className="overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
        {groupActivities ? (
          // Grouped View by Date
          <div className="space-y-2">
            {groupedActivities.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-custom-darkSilver font-medium">No activities found</div>
                <div className="text-custom-darkSilver text-sm">Try adjusting your filters</div>
              </div>
            ) : (
              groupedActivities.map((group) => {
                const isExpanded = expandedGroups.has(group.date);

                return (
                  <div key={group.date} className="rounded-lg overflow-hidden">
                    {/* Group Header */}
                    <div
                      className="group py-2 pl-2 pr-4 bg-custom-silver/50 transition-colors hover:bg-custom-blue/5"
                      onClick={() => toggleGroup(group.date)}
                    >
                      <div className="flex items-center justify-between cursor-pointer">
                        <div className="flex items-center space-x-2">
                          {isExpanded ? (
                            <ChevronDown className="w-5 h-5 text-custom-darkSilver transition group-hover:rotate-180" />
                          ) : (
                            <ChevronRight className="w-5 h-5 text-custom-darkSilver transition group-hover:translate-x-1" />
                          )}

                          <span className="text-custom-blue text-sm">
                            {formatDate(group.dateTimestamp || group.date)}
                          </span>
                          <div className="flex items-center text-xs text-custom-darkSilver">
                            <Clock className="w-3 h-3 mr-0.5" />
                            {getTimeSpan(group)}
                          </div>
                        </div>

                        <div className="text-sm text-custom-black">
                          {group.totalActivities} {group.totalActivities > 1 ? 'activities' : 'activity'}
                        </div>
                      </div>
                    </div>

                    {/* Group Activities */}
                    {isExpanded && (
                      <div className="bg-custom-silver/50 border-t border-custom-mediumSilver">
                        <ul className="py-2">
                          {group.activities.map((activity) => (
                            <li
                              key={activity.id}
                              className="flex items-center px-3 py-1 space-x-2 hover:bg-custom-lightSilver"
                            >
                              <div className="flex-shrink-0 w-2 h-2 ml-0.5 mr-1.5 bg-blue-500 rounded-full" />

                              <div className="flex-1">
                                <div className="flex items-center justify-between text-xs">
                                  <div className="flex-1">
                                    <div className="text-custom-black font-semibold">
                                      {formatEventType(activity.eventType)}
                                    </div>
                                    <div className="flex items-center space-x-4 mt-1">
                                      <span className="text-custom-darkSilver text-xs">
                                        Workspace: {activity.workspaceId || 'N/A'}
                                      </span>
                                      <span className="text-custom-darkSilver text-xs">
                                        User: {activity.userEmail || 'N/A'}
                                      </span>
                                      {activityTimeMs(activity) > 0 && (
                                        <span className="text-custom-darkSilver text-xs">
                                          {formatTime(activityTimeMs(activity))}
                                        </span>
                                      )}
                                    </div>
                                    {activity.data && Object.keys(activity.data).length > 0 && (
                                      <div className="mt-1">
                                        <pre className="text-xs bg-gray-50 p-1 rounded overflow-auto max-h-16">
                                          {JSON.stringify(activity.data, null, 2)}
                                        </pre>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        ) : (
          // All Activities View (table format)
          <div className="rounded-lg border border-custom-silver overflow-hidden">
            <table className="w-full">
              <thead className="bg-custom-silver">
                <tr>
                  <th className="px-4 py-2 text-left text-sm text-custom-darkSilver font-medium">EVENT TYPE</th>
                  <th className="px-4 py-2 text-left text-sm text-custom-darkSilver font-medium">USER EMAIL</th>
                  <th className="px-4 py-2 text-left text-sm text-custom-darkSilver font-medium">TIMESTAMP</th>
                  <th className="px-4 py-2 text-left text-sm text-custom-darkSilver font-medium">DATA</th>
                </tr>
              </thead>
              <tbody>
                {filteredActivities?.length === 0 ? (
                  <tr className="group text-sm h-10">
                    <td colSpan={4} className="px-4 text-custom-darkSilver bg-white">
                      No activities found matching your filters
                    </td>
                  </tr>
                ) : (
                  filteredActivities?.map((activity) => (
                    <tr key={activity.id} className="group hover:cursor-pointer text-sm hover:bg-custom-blue/5 h-10">
                      <td className="px-4 group-hover:bg-custom-blue/5 group-hover:rounded-none text-custom-darkSilver bg-white">
                        {formatEventType(activity.eventType)}
                      </td>
                      <td className="px-4 group-hover:bg-custom-blue/5 group-hover:rounded-none text-custom-darkSilver bg-white">
                        <span>{activity.userEmail || '-'}</span>
                      </td>
                      <td className="px-4 group-hover:bg-custom-blue/5 group-hover:rounded-none text-custom-darkSilver bg-white">
                        {activityTimeMs(activity) > 0 ? (
                          <div className="flex flex-col">
                            <span>{new Date(activityTimeMs(activity)).toLocaleString()}</span>
                            <span className="text-xs text-custom-darkSilver">
                              <ReactTimeAgo date={new Date(activityTimeMs(activity))} locale="en-US" />
                            </span>
                          </div>
                        ) : (
                          <span className="text-gray-500">-</span>
                        )}
                      </td>
                      <td className="px-4 group-hover:bg-custom-blue/5 group-hover:rounded-none text-custom-darkSilver bg-white">
                        {activity.data && Object.keys(activity.data).length > 0 ? (
                          <div className="max-w-xs max-h-40 overflow-auto">
                            <pre className="text-xs bg-gray-50 p-2 rounded">
                              {JSON.stringify(activity.data, null, 2)}
                            </pre>
                          </div>
                        ) : (
                          <span className="text-gray-500">-</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </CustomCard>
  );
};

export default ActivityLogsPage;
